import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, getUser } from "../api";
import { navUrl } from "../lib/navUrl";
import { toast } from "sonner";

const eur = (c) => (typeof c === "number" && c > 0 ? `€ ${(c / 100).toFixed(2)}` : "—");

export const InPersonBooking = ({ proId, proName }) => {
  const { t, i18n } = useTranslation();
  const u = getUser();
  const [slots, setSlots] = useState(null);
  const [locs, setLocs] = useState(null);
  const [selDay, setSelDay] = useState(null);
  const [sel, setSel] = useState(null);
  const [selLoc, setSelLoc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // PERF (punto 6): le sedi arrivano da un endpoint LEGGERO (nome/città/indirizzo) -> render immediato della scelta sede.
    api.get(`/professionals/${proId}/in-person-locations`).then(({ data }) => setLocs(data)).catch(() => setLocs([]));
    // Le disponibilità (slot) sono caricate in parallelo: il loading resta solo nell'area calendario.
    api.get(`/professionals/${proId}/in-person-slots`).then(({ data }) => setSlots(data)).catch(() => setSlots([]));
  }, [proId]);

  const locKey = (s) => `${s.location_label || ""}|${s.nav_address || ""}`;
  const locations = useMemo(() => locs || [], [locs]);
  useEffect(() => { if (locations.length === 1) setSelLoc(locations[0].key); }, [locations]);
  const days = useMemo(() => {
    if (!slots) return [];
    const m = new Map(); const now = Date.now();
    for (const s of slots) {
      if (new Date(s.start_at).getTime() <= now) continue;
      if (locations.length > 1 && selLoc && locKey(s) !== selLoc) continue;  // §10: solo la sede scelta
      const k = s.local_date; if (!m.has(k)) m.set(k, []); m.get(k).push(s);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [slots, selLoc, locations]);
  useEffect(() => { if (days.length && !days.some(([k]) => k === selDay)) setSelDay(days[0][0]); }, [days, selDay]);

  const placeLabel = (s) => s.location_label;
  const pay = async () => {
    if (!u) {
      localStorage.setItem("pending_action", JSON.stringify({ type: "book", kind: "pro", id: proId }));
      window.location.href = `/login?next=${encodeURIComponent(`/professionals/${proId}`)}`; return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/create-in-person-checkout", { professional_id: proId, slot_start: sel.start_at, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || t("events.error_generic")); setBusy(false); }
  };

  const selSlots = days.find(([k]) => k === selDay)?.[1] || [];
  return (
    <div data-testid="inperson-booking">
      <h2 className="text-xl font-bold mb-3">{t("inperson.type_inperson")}</h2>
      {locs === null && <div className="text-slate-400" data-testid="inperson-loc-loading">{t("common.loading")}</div>}
      {locations.length > 1 && (
        <div className="mb-3" data-testid="inperson-locations">
          <label htmlFor="inperson-loc-select" className="text-sm font-medium mb-1 block">{t("inperson.choose_location", "Scegli la sede")}</label>
          <select id="inperson-loc-select" data-testid="inperson-location-select" value={selLoc || ""}
            onChange={(e) => { setSelLoc(e.target.value || null); setSelDay(null); setSel(null); }}
            className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400 outline-none">
            <option value="">{t("inperson.choose_location", "Scegli la sede")}…</option>
            {locations.map(l => (
              <option key={l.key} value={l.key}>📍 {l.label}{l.address && l.address !== l.label ? ` — ${l.address}` : ""}{l.city && !(l.address || "").includes(l.city) ? ` (${l.city})` : ""}</option>))}
          </select>
        </div>)}
      {locations.length === 1 && locations.find(l=>l.key===selLoc) && (
        <div className="mb-3 text-sm text-slate-600" data-testid="inperson-location-header">
          {(() => { const L = locations.find(l=>l.key===selLoc); return (<>📍 <span className="font-medium">{L?.label}</span>{L?.address && L.address !== L.label ? ` · ${L.address}` : ""}{L?.city && !(L.address || L.label || "").includes(L.city) ? ` · ${L.city}` : ""}</>); })()}
        </div>)}
      {locs === null || (locations.length > 1 && !selLoc) ? null :
       slots === null ? <div className="text-slate-400">{t("common.loading")}</div>
        : days.length === 0 ? <p className="text-slate-500" data-testid="inperson-no-slots">{t("inperson.no_slots_location")}</p>
          : (<>
            <div className="flex gap-2 overflow-x-auto pb-2" role="tablist" data-testid="inperson-days">
              {days.map(([k]) => (
                <button key={k} role="tab" aria-selected={selDay === k} onClick={() => setSelDay(k)} data-testid="inperson-day"
                  className={`yp-btn !py-1.5 !px-3 text-sm shrink-0 ${selDay === k ? "yp-btn-primary" : "yp-btn-outline"}`}>
                  {new Date(`${k}T12:00:00`).toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" })}
                </button>))}
            </div>
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="inperson-times">
              {selSlots.map((s, i) => (
                <button key={i} onClick={() => setSel(s)} data-testid="inperson-slot-btn"
                  className="yp-card p-2 text-sm text-center hover:border-blue-400 transition-colors">
                  {s.local_time}
                </button>))}
            </div>
          </>)}

      {sel && (
        <div className="fixed inset-0 z-[9998] bg-black/50 flex items-center justify-center p-4" onClick={() => setSel(null)} data-testid="inperson-summary-overlay">
          <div className="yp-card p-6 max-w-sm w-full bg-white" onClick={(e) => e.stopPropagation()} data-testid="inperson-summary">
            <h3 className="text-lg font-bold mb-3">{t("inperson.summary")}</h3>
            <dl className="text-sm space-y-1">
              <div className="flex justify-between"><dt className="text-slate-500">{t("inperson.type_inperson")}</dt><dd className="font-medium">✓</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">{t("inperson.professional")}</dt><dd className="font-medium">{proName}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">{t("inperson.datetime")}</dt><dd data-testid="inperson-sum-when">{new Date(sel.start_at).toLocaleString(i18n.language)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">{t("inperson.duration")}</dt><dd>{sel.duration_minutes} min</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">📍</dt><dd className="font-medium text-right" data-testid="inperson-sum-place">{placeLabel(sel)}</dd></div>
              <div className="flex justify-between border-t border-slate-100 pt-2 mt-2"><dt className="font-semibold">{t("inperson.total")}</dt><dd className="font-semibold" data-testid="inperson-sum-total">{eur(sel.price_cents)}</dd></div>
            </dl>
            {navUrl(sel.nav_address || sel.location_label) && (
              <a href={navUrl(sel.nav_address || sel.location_label)} target="_blank" rel="noopener noreferrer"
                className="yp-btn yp-btn-outline w-full text-sm mt-3 inline-block text-center" data-testid="inperson-open-maps">📍 {t("inperson.open_maps")}</a>)}
            <div className="flex gap-2 mt-4">
              <button className="yp-btn yp-btn-outline flex-1" onClick={() => setSel(null)} data-testid="inperson-cancel">{t("common.cancel") || "×"}</button>
              <button className="yp-btn yp-btn-primary flex-1" onClick={pay} disabled={busy} data-testid="inperson-pay-btn">{t("inperson.book")}</button>
            </div>
          </div>
        </div>)}
    </div>
  );
};
